import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import type { RoleDefinition } from "@/lib/auth";
import { useMemo } from "react";
import { canOpenPeopleAccess, type AccessLocation, type AccessPerson, type PersonPatch } from "./model";

async function scopedPeople(workspaceId: string, signal?: AbortSignal): Promise<AccessPerson[]> {
  const response = await fetch("/api/people-access/people", { signal, credentials: "include", headers: { "x-workspace-id": workspaceId } });
  if (!response.ok) throw new Error(`Could not load people (${response.status})`);
  return response.json();
}
async function readSettings(workspaceId: string, signal?: AbortSignal): Promise<{ canAdd: boolean; canManageSettings: boolean }> {
  const response = await fetch("/api/people-access/settings", { signal, credentials: "include", headers: { "x-workspace-id": workspaceId } });
  if (!response.ok) throw new Error(`Could not load access settings (${response.status})`);
  return response.json();
}

export function usePeopleAccess(){
  const auth=useAuth();
  const queryClient=useQueryClient();
  const workspaceId=auth.currentWorkspace?.id;
  const actorId=auth.user?.id;
  const allowed=canOpenPeopleAccess(auth.user,auth.roles);
  const enabled=!!workspaceId && !auth.isLoading;
  const key=["/api/people-access/people",workspaceId,actorId];
  const settings=useQuery({queryKey:["/api/people-access/settings",workspaceId,actorId],queryFn:({signal})=>readSettings(workspaceId!,signal),enabled:enabled&&!!actorId,staleTime:20_000});
  const pageAllowed=allowed || !!settings.data?.canAdd || !!settings.data?.canManageSettings;
  const roleQuery=useQuery<RoleDefinition[]>({
    queryKey:["/api/people-access/roles",workspaceId,actorId],
    queryFn:async({signal})=>{
      const response=await fetch("/api/people-access/roles",{signal,credentials:"include",headers:{"x-workspace-id":workspaceId!}});
      if(!response.ok) throw new Error("Could not load workspace roles");
      return response.json();
    },enabled:enabled&&!!actorId&&pageAllowed,staleTime:30_000,
  });
  const roles=useMemo(()=>roleQuery.data||auth.roles.filter(role=>role.workspaceId===null || role.workspaceId===workspaceId),[roleQuery.data,auth.roles,workspaceId]);
  const users=useQuery<AccessPerson[]>({queryKey:key,queryFn:({signal})=>scopedPeople(workspaceId!,signal),enabled:enabled&&!!actorId&&pageAllowed,staleTime:30_000});
  const locations=useQuery<AccessLocation[]>({
    queryKey:["/api/locations",workspaceId,"people-access",actorId],
    queryFn:()=>api.locations.list(workspaceId!),enabled:enabled&&!!actorId&&pageAllowed,staleTime:60_000,
  });
  // The existing PATCH endpoint explicitly requires the stored legacy grant for
  // assignment editing. Do not silently broaden it to match the UI aliases.
  const canEdit=!!auth.roles.find(role=>role.id===auth.user?.roleId)?.permissions.some(perm=>perm.id==="manage_users");
  const mutation=useMutation({
    mutationFn:async({person,patch}:{person:AccessPerson;patch:PersonPatch})=>{
       if(!pageAllowed || !canEdit || !workspaceId) throw new Error("Existing user-management authority is required to change assignments.");
      await auth.updateUser({id:person.id,...patch} as AccessPerson);
    },
    onMutate:async({person,patch})=>{
      await queryClient.cancelQueries({queryKey:key});
      const previous=queryClient.getQueryData<AccessPerson[]>(key);
      queryClient.setQueryData<AccessPerson[]>(key,rows=>rows?.map(row=>row.id===person.id?{...row,...patch}:row));
      return {previous};
    },
    onError:(_error,_variables,context)=>{if(context?.previous)queryClient.setQueryData(key,context.previous)},
    onSettled:async()=>{
      await queryClient.invalidateQueries({queryKey:key});
      await queryClient.invalidateQueries({queryKey:["/api/me"]});
    },
  });
  return {
    people:users.data||[], roles,
    locations:locations.data||[],allowed:pageAllowed,canManageSettings:!!settings.data?.canManageSettings,canAdd:!!settings.data?.canAdd,canEdit,isLoading:auth.isLoading||(enabled&&(settings.isPending||(pageAllowed&&(users.isPending||roleQuery.isPending||locations.isPending)))),
    error:(users.error||locations.error||roleQuery.error||settings.error) as Error|null,isSaving:mutation.isPending,
    savePerson:(person:AccessPerson,patch:PersonPatch)=>mutation.mutateAsync({person,patch}),
    retry:()=>{void users.refetch();void locations.refetch();void settings.refetch()},
  };
}

export function useCanAccessPeopleAccess() {
  const auth = useAuth();
  const workspaceId = auth.currentWorkspace?.id;
  const actorId = auth.user?.id;
  const query = useQuery<{ canAdd: boolean; canManageSettings: boolean }>({
    queryKey: ["/api/people-access/settings", workspaceId, actorId],
    queryFn: async ({signal}) => {
      const response = await fetch("/api/people-access/settings", { signal, credentials: "include", headers: { "x-workspace-id": workspaceId! } });
      if (!response.ok) throw new Error(`Could not load people access capability (${response.status})`);
      return response.json();
    },
    enabled: !!workspaceId && !!actorId && !auth.isLoading,
    staleTime: 30_000,
  });
  return !!query.data?.canAdd || !!query.data?.canManageSettings;
}
